import { translateAddress, utils } from "@project-serum/anchor";
import { ChainId, createMintInstructions, Token, TOKEN_PROGRAM_ID, TokenAmount, } from "@saberhq/token-utils";
import { Keypair, PublicKey, SystemProgram, SYSVAR_RENT_PUBKEY, } from "@solana/web3.js";
import { SABER_ADDRESSES } from "../../constants";
async function associated(programId, ...args) {
    const seeds = [Buffer.from([97, 110, 99, 104, 111, 114])]; // b"anchor".
    args.forEach((arg) => {
        seeds.push(arg instanceof Buffer ? arg : translateAddress(arg).toBuffer());
    });
    const [assoc] = await PublicKey.findProgramAddress(seeds, translateAddress(programId));
    return assoc;
}
/**
 * Finds the address of a minter.
 * @param minter
 * @returns
 */
export const findMinterInfoAddress = async (minter) => {
    return await associated(SABER_ADDRESSES.MintProxy, minter);
};
export class MintProxyWrapper {
    constructor(saber) {
        this.saber = saber;
        this.program = saber.programs.MintProxy;
    }
    get provider() {
        return this.saber.provider;
    }
    async getProxyMintAuthority() {
        const stateAccount = this.program.state.address();
        return await PublicKey.findProgramAddress([utils.bytes.utf8.encode("SaberMintProxy"), stateAccount.toBuffer()], this.program.programId);
    }
    async new({ hardcap, mintAuthority, tokenMint, tokenProgram = TOKEN_PROGRAM_ID, owner = this.provider.wallet.publicKey, }) {
        const [proxyMintAuthority, nonce] = await this.getProxyMintAuthority();
        const instructions = [];
        instructions.push(this.program.state.instruction.new(nonce, hardcap, {
            accounts: {
                mintAuthority,
                proxyMintAuthority,
                owner,
                tokenMint,
                tokenProgram,
            },
        }));
        return {
            proxyAuthority: proxyMintAuthority,
            tx: this.saber.newTx(instructions),
        };
    }
    async createMintWithProxy(hardcap, mintKey = Keypair.generate().publicKey, decimals, owner = null) {
        const provider = this.saber.provider;
        owner = owner !== null && owner !== void 0 ? owner : provider.wallet.publicKey;
        const mintInstructions = await createMintInstructions(provider, owner, mintKey, decimals);
        const [proxyMintAuthority] = await this.getProxyMintAuthority();
        const tx = await this.initMintProxy({
            hardCap: new TokenAmount(new Token({
                address: mintKey.toString(),
                chainId: ChainId.MainnetBeta,
                decimals,
                name: "Saber Protocol Token",
                symbol: "SBR",
            }), hardcap),
        });
        tx.instructions.unshift(...mintInstructions);
        return {
            mint: mintKey,
            proxyAuthority: proxyMintAuthority,
            tx,
        };
    }
    /**
     * Initializes the mint proxy with the given hardcap.
     *
     * The provider must be the mint authority and the owner of the mint.
     *
     * @returns
     */
    async initMintProxy({ hardCap, }) {
        const owner = this.provider.wallet.publicKey;
        const [proxyMintAuthority, nonce] = await this.getProxyMintAuthority();
        const instructions = [
            this.program.state.instruction.new(nonce, hardCap.toU64(), {
                accounts: {
                    mintAuthority: owner,
                    proxyMintAuthority,
                    owner,
                    tokenMint: hardCap.token.mintAccount,
                    tokenProgram: TOKEN_PROGRAM_ID,
                },
            }),
        ];
        return this.saber.newTx(instructions);
    }
    async getMinterInfoAddress(minter) {
        return await this.program.account.minterInfo.associatedAddress(minter);
    }
    /**
     * Fetches info on a minter.
     * @param minter
     * @returns
     */
    async fetchMinterInfo(minter) {
        const minterInfoAddress = await this.getMinterInfoAddress(minter);
        const accountInfo = await this.provider.connection.getAccountInfo(minterInfoAddress);
        if (!accountInfo) {
            return null;
        }
        return this.program.coder.accounts.decode("MinterInfo", accountInfo.data);
    }
    async minterAdd(minter, allowance, owner = this.provider.wallet.publicKey) {
        const minterInfo = await this.program.account.minterInfo.associatedAddress(minter);
        const ix = this.program.state.instruction.minterAdd(allowance, {
            accounts: {
                auth: { owner },
                minter,
                minterInfo,
                payer: this.provider.wallet.publicKey,
                rent: SYSVAR_RENT_PUBKEY,
                systemProgram: SystemProgram.programId,
            },
        });
        return this.saber.newTx([ix]);
    }
    /**
     * Updates a minter's allowance.
     * @param minter
     * @param allowance
     * @returns
     */
    async minterUpdate(minter, allowance) {
        const minterInfo = await this.program.account.minterInfo.associatedAddress(minter);
        const ix = this.program.state.instruction.minterUpdate(allowance, {
            accounts: {
                auth: { owner: this.provider.wallet.publicKey },
                minterInfo,
            },
        });
        return this.saber.newTx([ix]);
    }
    async minterRemove(minter, owner = this.provider.wallet.publicKey) {
        const minterInfo = await this.program.account.minterInfo.associatedAddress(minter);
        const ix = this.program.state.instruction.minterRemove({
            accounts: {
                auth: { owner },
                minter,
                minterInfo,
                payer: this.provider.wallet.publicKey,
            },
        });
        return this.saber.newTx([ix]);
    }
    transferOwnership(nextOwner) {
        return this.saber.newTx([
            this.program.state.instruction.transferOwnership(nextOwner, {
                accounts: { owner: this.provider.wallet.publicKey },
            }),
        ]);
    }
    acceptOwnership() {
        return this.saber.newTx([
            this.program.state.instruction.acceptOwnership({
                accounts: { owner: this.provider.wallet.publicKey },
            }),
        ]);
    }
    async mintProxyExists() {
        try {
            await this.program.state.fetch();
        }
        catch (e) {
            return false;
        }
        return true;
    }
}
//# sourceMappingURL=index.js.map